import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import {chain} from 'lodash';
import React from 'react';

import {useFilteredProjectPickerQueryQuery} from './../../generated/graphql';
import {useViewer} from './../../state/viewer/hooks';

export interface ProjectData {
  name: string;
  entityName: string;
  internalId: string;
}

export const useProjectOptions = ({
  selectedProject,
  queryString,
}: {
  selectedProject: ProjectData;
  queryString: string;
}): {
  loading: boolean;
  projects: ProjectData[];
  selectedProjectIdx: number;
} => {
  const viewer = useViewer();
  const {data, loading} = useFilteredProjectPickerQueryQuery({
    variables: {
      entityName: selectedProject.entityName,
      userName: viewer?.username ?? '',
      pattern: queryString,
    },
  });

  const entityProjects = data?.entity?.projects?.edges.map(e => e.node) ?? [];
  const viewerProjects = data?.viewer?.projects?.edges.map(e => e.node) ?? [];
  const userProjects =
    data?.user?.teams?.edges.flatMap(edge =>
      edge.node?.projects?.edges.map(e => e.node)
    ) ?? [];

  // query results are divided into projects for entity, viewer, and user.
  // the same project may appear in multiple categories, so we need to combine and de-dupe.
  let projects = chain(userProjects)
    .concat(viewerProjects)
    .concat(entityProjects)
    .filter(isNotNullOrUndefined)
    .uniqBy(p => p.internalId)
    .orderBy(p => p.createdAt, 'desc')
    .map(p => ({
      entityName: p.entityName,
      name: p.name,
      internalId: p.internalId,
    }))
    .value();

  // if selected project is not in the query results, insert it at the top of the list
  let selectedProjectIdx = projects.findIndex(
    p => p.internalId === selectedProject.internalId
  );
  if (selectedProjectIdx === -1) {
    projects = [selectedProject, ...projects];
    selectedProjectIdx = 0;
  }

  return React.useMemo(
    () => ({
      loading,
      projects,
      selectedProjectIdx,
    }),
    [projects, loading, selectedProjectIdx]
  );
};
